/** @module @liquidefy/code-editor/git/git-staging @version 1.0.0 @license LQD Research LLC */

import type {
  EditorResult,
  GitStagingHunk,
  GitDiffLine,
} from '../../code-editor-types.js';

import {
  ok,
  err,
} from '../../code-editor.js';

// ═══ §1 IMPORTS — re-exported above ═══

// ═══ §2 DIFF PARSING — parse unified diff output into structured hunks ═══

export function parseDiffOutput(output: string): EditorResult<GitStagingHunk[]> {
  const hunks: GitStagingHunk[] = [];
  const lines = output.split('\n');
  let currentHunk: { header: string; oldStart: number; oldCount: number; newStart: number; newCount: number; lines: GitDiffLine[] } | undefined;
  let oldLine = 0;
  let newLine = 0;

  for (const line of lines) {
    const hunkMatch = line.match(/^@@\s+-(\d+)(?:,(\d+))?\s+\+(\d+)(?:,(\d+))?\s+@@(.*)$/);
    if (hunkMatch) {
      if (currentHunk) hunks.push(currentHunk);
      const oStart = parseInt(hunkMatch[1]!, 10);
      const oCount = parseInt(hunkMatch[2] ?? '1', 10);
      const nStart = parseInt(hunkMatch[3]!, 10);
      const nCount = parseInt(hunkMatch[4] ?? '1', 10);
      oldLine = oStart;
      newLine = nStart;
      currentHunk = {
        header: line,
        oldStart: oStart,
        oldCount: oCount,
        newStart: nStart,
        newCount: nCount,
        lines: [],
      };
      continue;
    }
    if (!currentHunk) continue;
    if (line.startsWith('+')) {
      currentHunk.lines.push({
        type: 'add',
        content: line.substring(1),
        oldLineNumber: undefined,
        newLineNumber: newLine,
      });
      newLine++;
    } else if (line.startsWith('-')) {
      currentHunk.lines.push({
        type: 'delete',
        content: line.substring(1),
        oldLineNumber: oldLine,
        newLineNumber: undefined,
      });
      oldLine++;
    } else if (line.startsWith(' ') || line === '') {
      const content = line.length > 0 ? line.substring(1) : '';
      currentHunk.lines.push({
        type: 'context',
        content,
        oldLineNumber: oldLine,
        newLineNumber: newLine,
      });
      oldLine++;
      newLine++;
    }
  }
  if (currentHunk) hunks.push(currentHunk);
  return ok(hunks, 'DIFF_PARSED', `Parsed ${hunks.length} hunk(s)`);
}

export function parseHunkHeader(
  header: string
): EditorResult<{ oldStart: number; oldCount: number; newStart: number; newCount: number }> {
  const match = header.match(/^@@\s+-(\d+)(?:,(\d+))?\s+\+(\d+)(?:,(\d+))?\s+@@/);
  if (!match) return err('INVALID_HUNK_HEADER', `Cannot parse hunk header: "${header}"`);
  return ok(
    {
      oldStart: parseInt(match[1]!, 10),
      oldCount: parseInt(match[2] ?? '1', 10),
      newStart: parseInt(match[3]!, 10),
      newCount: parseInt(match[4] ?? '1', 10),
    },
    'HUNK_HEADER_PARSED',
    'Parsed hunk header'
  );
}

export function parseDiffLine(line: string): EditorResult<GitDiffLine> {
  if (line.startsWith('+')) {
    return ok({ type: 'add', content: line.substring(1), oldLineNumber: undefined, newLineNumber: undefined }, 'DIFF_LINE_PARSED', 'Parsed add line');
  }
  if (line.startsWith('-')) {
    return ok({ type: 'delete', content: line.substring(1), oldLineNumber: undefined, newLineNumber: undefined }, 'DIFF_LINE_PARSED', 'Parsed delete line');
  }
  return ok({ type: 'context', content: line.startsWith(' ') ? line.substring(1) : line, oldLineNumber: undefined, newLineNumber: undefined }, 'DIFF_LINE_PARSED', 'Parsed context line');
}

export function parsePatchHeader(
  output: string
): EditorResult<{ oldPath: string; newPath: string; isRename: boolean }> {
  const lines = output.split('\n');
  let oldPath = '';
  let newPath = '';
  for (const line of lines) {
    if (line.startsWith('--- a/')) oldPath = line.substring(6);
    else if (line.startsWith('--- /dev/null')) oldPath = '/dev/null';
    else if (line.startsWith('+++ b/')) newPath = line.substring(6);
    else if (line.startsWith('+++ /dev/null')) newPath = '/dev/null';
    else if (line.startsWith('rename from ')) oldPath = line.substring(12);
    else if (line.startsWith('rename to ')) newPath = line.substring(10);
  }
  const isRename = oldPath !== '' && newPath !== '' && oldPath !== newPath && oldPath !== '/dev/null' && newPath !== '/dev/null';
  return ok({ oldPath, newPath, isRename }, 'PATCH_HEADER_PARSED', 'Parsed patch header');
}

export function isDiffBinary(output: string): boolean {
  return output.includes('Binary files') || output.includes('GIT binary patch');
}

export function isNewFile(output: string): boolean {
  return output.includes('--- /dev/null') && output.includes('+++ b/');
}

export function isDeletedFile(output: string): boolean {
  return output.includes('--- a/') && output.includes('+++ /dev/null');
}

export function isRenamedFile(output: string): boolean {
  return output.includes('rename from') && output.includes('rename to');
}

// ═══ §3 HUNK OPERATIONS — split, merge, reverse, inspect hunks ═══

export function splitHunk(
  hunk: GitStagingHunk,
  selectedLines: number[]
): EditorResult<{ staged: GitStagingHunk; unstaged: GitStagingHunk }> {
  const selectedSet = new Set(selectedLines);
  const stagedLines: GitDiffLine[] = [];
  const unstagedLines: GitDiffLine[] = [];

  for (let i = 0; i < hunk.lines.length; i++) {
    const line = hunk.lines[i];
    if (line!.type === 'context') {
      stagedLines.push(line!);
      unstagedLines.push(line!);
    } else if (selectedSet.has(i)) {
      stagedLines.push(line!);
      if (line!.type === 'delete') {
        // Unstaged side keeps the line as context
        unstagedLines.push({ ...line!, type: 'context' });
      }
      // For 'add', unstaged side simply omits it
    } else {
      unstagedLines.push(line!);
      if (line!.type === 'add') {
        // Staged side omits unselected additions
      } else if (line!.type === 'delete') {
        stagedLines.push({ ...line!, type: 'context' });
      }
    }
  }

  const stagedAdditions = stagedLines.filter((l) => l.type === 'add').length;
  const stagedDeletions = stagedLines.filter((l) => l.type === 'delete').length;
  const unstagedAdditions = unstagedLines.filter((l) => l.type === 'add').length;
  const unstagedDeletions = unstagedLines.filter((l) => l.type === 'delete').length;

  const staged: GitStagingHunk = {
    header: `@@ -${hunk.oldStart},${hunk.oldCount - stagedDeletions + stagedDeletions} +${hunk.newStart},${hunk.oldCount - stagedDeletions + stagedAdditions} @@`,
    oldStart: hunk.oldStart,
    oldCount: hunk.oldCount,
    newStart: hunk.newStart,
    newCount: hunk.oldCount - stagedDeletions + stagedAdditions,
    lines: stagedLines,
  };

  const unstaged: GitStagingHunk = {
    header: `@@ -${hunk.oldStart},${hunk.oldCount - unstagedDeletions + unstagedDeletions} +${hunk.newStart},${hunk.oldCount - unstagedDeletions + unstagedAdditions} @@`,
    oldStart: hunk.oldStart,
    oldCount: hunk.oldCount,
    newStart: hunk.newStart,
    newCount: hunk.oldCount - unstagedDeletions + unstagedAdditions,
    lines: unstagedLines,
  };

  return ok({ staged, unstaged }, 'HUNK_SPLIT', `Split hunk into ${selectedLines.length} staged lines`);
}

export function mergeHunks(hunks: GitStagingHunk[]): EditorResult<GitStagingHunk> {
  if (hunks.length === 0) return err('NO_HUNKS', 'No hunks to merge');
  if (hunks.length === 1) return ok(hunks[0]!, 'HUNK_SINGLE', 'Single hunk, no merge needed');
  const sorted = [...hunks].sort((a, b) => a.oldStart - b.oldStart);
  const allLines: GitDiffLine[] = [];
  for (const hunk of sorted) {
    allLines.push(...hunk.lines);
  }
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const totalAdditions = allLines.filter((l) => l.type === 'add').length;
  const totalDeletions = allLines.filter((l) => l.type === 'delete').length;
  const totalContext = allLines.filter((l) => l.type === 'context').length;
  const merged: GitStagingHunk = {
    header: `@@ -${first!.oldStart},${totalContext + totalDeletions} +${first!.newStart},${totalContext + totalAdditions} @@`,
    oldStart: first!.oldStart,
    oldCount: totalContext + totalDeletions,
    newStart: first!.newStart,
    newCount: totalContext + totalAdditions,
    lines: allLines,
  };
  return ok(merged, 'HUNKS_MERGED', `Merged ${hunks.length} hunks`);
}

export function reverseHunk(hunk: GitStagingHunk): EditorResult<GitStagingHunk> {
  const reversedLines: GitDiffLine[] = hunk.lines.map((line) => {
    if (line.type === 'add') return { ...line, type: 'delete' as const, oldLineNumber: line.newLineNumber, newLineNumber: undefined };
    if (line.type === 'delete') return { ...line, type: 'add' as const, newLineNumber: line.oldLineNumber, oldLineNumber: undefined };
    return line;
  });
  const reversed: GitStagingHunk = {
    header: `@@ -${hunk.newStart},${hunk.newCount} +${hunk.oldStart},${hunk.oldCount} @@`,
    oldStart: hunk.newStart,
    oldCount: hunk.newCount,
    newStart: hunk.oldStart,
    newCount: hunk.oldCount,
    lines: reversedLines,
  };
  return ok(reversed, 'HUNK_REVERSED', 'Reversed hunk for unstaging');
}

export function hunkLineCount(hunk: GitStagingHunk): number {
  return hunk.lines.length;
}

export function hunkAdditions(hunk: GitStagingHunk): number {
  return hunk.lines.filter((l) => l.type === 'add').length;
}

export function hunkDeletions(hunk: GitStagingHunk): number {
  return hunk.lines.filter((l) => l.type === 'delete').length;
}

export function isHunkEmpty(hunk: GitStagingHunk): boolean {
  return hunk.lines.every((l) => l.type === 'context');
}

export function hunkToString(hunk: GitStagingHunk): string {
  const lineTexts = hunk.lines.map((line) => {
    if (line.type === 'add') return '+' + line.content;
    if (line.type === 'delete') return '-' + line.content;
    return ' ' + line.content;
  });
  return hunk.header + '\n' + lineTexts.join('\n');
}

// ═══ §4 PARTIAL STAGING — stage/unstage specific lines and hunks ═══

export function stageLines(
  hunk: GitStagingHunk,
  lineNumbers: number[]
): EditorResult<GitStagingHunk> {
  const selectedSet = new Set(lineNumbers);
  const patchLines: GitDiffLine[] = [];
  for (let i = 0; i < hunk.lines.length; i++) {
    const line = hunk.lines[i];
    if (line!.type === 'context') {
      patchLines.push(line!);
    } else if (selectedSet.has(i)) {
      patchLines.push(line!);
    } else if (line!.type === 'delete') {
      patchLines.push({ ...line!, type: 'context' });
    }
    // Unselected 'add' lines are omitted
  }
  const additions = patchLines.filter((l) => l.type === 'add').length;
  const deletions = patchLines.filter((l) => l.type === 'delete').length;
  const context = patchLines.filter((l) => l.type === 'context').length;
  const result: GitStagingHunk = {
    header: `@@ -${hunk.oldStart},${context + deletions} +${hunk.newStart},${context + additions} @@`,
    oldStart: hunk.oldStart,
    oldCount: context + deletions,
    newStart: hunk.newStart,
    newCount: context + additions,
    lines: patchLines,
  };
  return ok(result, 'LINES_STAGED', `Staged ${lineNumbers.length} line(s)`);
}

export function unstageLines(
  hunk: GitStagingHunk,
  lineNumbers: number[]
): EditorResult<GitStagingHunk> {
  const reversed = reverseHunk(hunk);
  if (!reversed.success) return reversed;
  return stageLines(reversed.value, lineNumbers);
}

export function stageHunks(
  hunks: GitStagingHunk[],
  hunkIndices: number[]
): EditorResult<GitStagingHunk[]> {
  const selectedSet = new Set(hunkIndices);
  const selected = hunks.filter((_, i) => selectedSet.has(i));
  if (selected.length === 0) return err('NO_HUNKS_SELECTED', 'No hunks selected for staging');
  return ok(selected, 'HUNKS_STAGED', `Staged ${selected.length} hunk(s)`);
}

export function unstageHunks(
  hunks: GitStagingHunk[],
  hunkIndices: number[]
): EditorResult<GitStagingHunk[]> {
  const selectedSet = new Set(hunkIndices);
  const selected: GitStagingHunk[] = [];
  for (let i = 0; i < hunks.length; i++) {
    if (!selectedSet.has(i)) continue;
    const reversed = reverseHunk(hunks[i]!);
    if (reversed.success) selected.push(reversed.value);
  }
  if (selected.length === 0) return err('NO_HUNKS_SELECTED', 'No hunks selected for unstaging');
  return ok(selected, 'HUNKS_UNSTAGED', `Unstaged ${selected.length} hunk(s)`);
}

export function computeStagePatch(
  hunks: GitStagingHunk[],
  selectedLines: Map<number, number[]>
): EditorResult<string> {
  const patchParts: string[] = [];
  for (let hi = 0; hi < hunks.length; hi++) {
    const lineNums = selectedLines.get(hi);
    if (!lineNums || lineNums.length === 0) continue;
    const result = stageLines(hunks[hi]!, lineNums);
    if (result.success) {
      patchParts.push(hunkToString(result.value));
    }
  }
  if (patchParts.length === 0) return err('EMPTY_PATCH', 'No lines selected for patch');
  return ok(patchParts.join('\n'), 'PATCH_COMPUTED', `Computed patch with ${patchParts.length} hunk(s)`);
}

export function validatePatch(patch: string): EditorResult<boolean> {
  const lines = patch.split('\n');
  let inHunk = false;
  let oldCount = 0;
  let newCount = 0;
  let expectedOld = 0;
  let expectedNew = 0;
  const errors: string[] = [];

  for (const line of lines) {
    const hunkMatch = line.match(/^@@\s+-(\d+)(?:,(\d+))?\s+\+(\d+)(?:,(\d+))?\s+@@/);
    if (hunkMatch) {
      if (inHunk) {
        if (oldCount !== expectedOld) errors.push(`Old line count mismatch: expected ${expectedOld}, got ${oldCount}`);
        if (newCount !== expectedNew) errors.push(`New line count mismatch: expected ${expectedNew}, got ${newCount}`);
      }
      inHunk = true;
      expectedOld = parseInt(hunkMatch[2] ?? '1', 10);
      expectedNew = parseInt(hunkMatch[4] ?? '1', 10);
      oldCount = 0;
      newCount = 0;
      continue;
    }
    if (!inHunk) continue;
    if (line.startsWith('+')) { newCount++; }
    else if (line.startsWith('-')) { oldCount++; }
    else if (line.startsWith(' ')) { oldCount++; newCount++; }
  }
  if (inHunk) {
    if (oldCount !== expectedOld) errors.push(`Old line count mismatch: expected ${expectedOld}, got ${oldCount}`);
    if (newCount !== expectedNew) errors.push(`New line count mismatch: expected ${expectedNew}, got ${newCount}`);
  }
  if (errors.length > 0) return err('INVALID_PATCH', errors.join('; '));
  return ok(true, 'PATCH_VALID', 'Patch is valid');
}

// ═══ §5 PATCH OPERATIONS — apply, create, format patches ═══

export function applyPatch(content: string, hunks: GitStagingHunk[]): EditorResult<string> {
  const lines = content.split('\n');
  const sortedHunks = [...hunks].sort((a, b) => b.oldStart - a.oldStart);
  const result = [...lines];

  for (const hunk of sortedHunks) {
    const startIdx = hunk.oldStart - 1;
    const removeLines: number[] = [];
    const addLines: { index: number; content: string }[] = [];
    let offset = 0;
    for (const diffLine of hunk.lines) {
      if (diffLine.type === 'delete') {
        removeLines.push(startIdx + offset);
        offset++;
      } else if (diffLine.type === 'add') {
        addLines.push({ index: startIdx + offset, content: diffLine.content });
      } else {
        offset++;
      }
    }
    // Remove deleted lines in reverse order
    for (let i = removeLines.length - 1; i >= 0; i--) {
      const idx = removeLines[i]!;
      if (idx >= 0 && idx < result.length) result.splice(idx, 1);
    }
    // Add new lines
    let insertOffset = 0;
    for (const add of addLines) {
      const idx = Math.min(add.index - removeLines.filter((r) => r! < add.index).length + insertOffset, result.length);
      result.splice(idx, 0, add.content);
      insertOffset++;
    }
  }
  return ok(result.join('\n'), 'PATCH_APPLIED', `Applied ${hunks.length} hunk(s)`);
}

export function createPatch(
  original: string,
  modified: string,
  context: number = 3
): EditorResult<GitStagingHunk[]> {
  const origLines = original.split('\n');
  const modLines = modified.split('\n');
  const hunks: GitStagingHunk[] = [];

  // Simple line-by-line diff (Myers-like for basic cases)
  const maxLen = Math.max(origLines.length, modLines.length);
  const changes: { type: 'context' | 'add' | 'delete'; origIdx: number; modIdx: number; content: string }[] = [];

  let oi = 0;
  let mi = 0;
  while (oi < origLines.length || mi < modLines.length) {
    if (oi < origLines.length && mi < modLines.length && origLines[oi] === modLines[mi]) {
      changes.push({ type: 'context', origIdx: oi, modIdx: mi, content: origLines[oi]! });
      oi++;
      mi++;
    } else if (oi < origLines.length && (mi >= modLines.length || origLines[oi] !== modLines[mi])) {
      // Check if this line exists later in modified
      const futureIdx = modLines.indexOf(origLines[oi]!, mi);
      if (futureIdx >= 0 && futureIdx - mi < 5) {
        // Add the intervening modified lines first
        while (mi < futureIdx) {
          changes.push({ type: 'add', origIdx: oi, modIdx: mi, content: modLines[mi]! });
          mi++;
        }
        changes.push({ type: 'context', origIdx: oi, modIdx: mi, content: origLines[oi]! });
        oi++;
        mi++;
      } else {
        changes.push({ type: 'delete', origIdx: oi, modIdx: mi, content: origLines[oi]! });
        oi++;
      }
    } else {
      changes.push({ type: 'add', origIdx: oi, modIdx: mi, content: modLines[mi]! });
      mi++;
    }
  }

  // Group changes into hunks with context
  let hunkStart = -1;
  let hunkLines: GitDiffLine[] = [];
  let oldStart = 1;
  let newStart = 1;

  for (let i = 0; i < changes.length; i++) {
    const c = changes[i];
    const isChange = c!.type !== 'context';
    const nearChange = isChange ||
      changes.slice(Math.max(0, i - context), i).some((x) => x.type !== 'context') ||
      changes.slice(i + 1, i + 1 + context).some((x) => x.type !== 'context');

    if (nearChange) {
      if (hunkStart === -1) {
        hunkStart = i;
        oldStart = c!.origIdx + 1;
        newStart = c!.modIdx + 1;
      }
      const dl: GitDiffLine = {
        type: c!.type,
        content: c!.content,
        oldLineNumber: c!.type !== 'add' ? c!.origIdx + 1 : undefined,
        newLineNumber: c!.type !== 'delete' ? c!.modIdx + 1 : undefined,
      };
      hunkLines.push(dl);
    } else if (hunkStart >= 0) {
      // End of hunk
      const oldCount = hunkLines.filter((l) => l.type !== 'add').length;
      const newCount = hunkLines.filter((l) => l.type !== 'delete').length;
      hunks.push({
        header: `@@ -${oldStart},${oldCount} +${newStart},${newCount} @@`,
        oldStart,
        oldCount,
        newStart,
        newCount,
        lines: hunkLines,
      });
      hunkStart = -1;
      hunkLines = [];
    }
  }

  if (hunkStart >= 0 && hunkLines.length > 0) {
    const oldCount = hunkLines.filter((l) => l.type !== 'add').length;
    const newCount = hunkLines.filter((l) => l.type !== 'delete').length;
    hunks.push({
      header: `@@ -${oldStart},${oldCount} +${newStart},${newCount} @@`,
      oldStart,
      oldCount,
      newStart,
      newCount,
      lines: hunkLines,
    });
  }

  return ok(hunks, 'PATCH_CREATED', `Created patch with ${hunks.length} hunk(s)`);
}

export function formatPatch(hunks: GitStagingHunk[], filePath: string): string {
  const header = `--- a/${filePath}\n+++ b/${filePath}`;
  const hunkTexts = hunks.map((h) => hunkToString(h));
  return header + '\n' + hunkTexts.join('\n');
}

export function patchStats(
  hunks: GitStagingHunk[]
): { additions: number; deletions: number; files: number } {
  let additions = 0;
  let deletions = 0;
  for (const hunk of hunks) {
    additions += hunkAdditions(hunk);
    deletions += hunkDeletions(hunk);
  }
  return { additions, deletions, files: 1 };
}

// ═══ §6 INTERACTIVE STAGING — suggest splits, context, highlight changes ═══

export function suggestHunkSplits(hunk: GitStagingHunk): number[][] {
  const groups: number[][] = [];
  let currentGroup: number[] = [];
  let lastChangeIdx = -10;

  for (let i = 0; i < hunk.lines.length; i++) {
    const line = hunk.lines[i];
    if (line!.type === 'context') continue;
    if (i - lastChangeIdx > 3 && currentGroup.length > 0) {
      groups.push(currentGroup);
      currentGroup = [];
    }
    currentGroup.push(i);
    lastChangeIdx = i;
  }
  if (currentGroup.length > 0) groups.push(currentGroup);
  return groups;
}

export function canSplitHunk(hunk: GitStagingHunk): boolean {
  const splits = suggestHunkSplits(hunk);
  return splits.length > 1;
}

export function hunkContext(
  hunk: GitStagingHunk,
  content: string
): { before: string[]; after: string[] } {
  const allLines = content.split('\n');
  const startIdx = Math.max(0, hunk.oldStart - 4);
  const endIdx = Math.min(allLines.length, hunk.oldStart + hunk.oldCount + 3);
  const before = allLines.slice(startIdx, hunk.oldStart - 1);
  const after = allLines.slice(hunk.oldStart + hunk.oldCount - 1, endIdx);
  return { before, after };
}

export interface CharChange {
  readonly lineIndex: number;
  readonly ranges: { start: number; end: number }[];
}

export function highlightChanges(hunk: GitStagingHunk): CharChange[] {
  const results: CharChange[] = [];
  const deletions: { idx: number; line: GitDiffLine }[] = [];
  const additions: { idx: number; line: GitDiffLine }[] = [];

  for (let i = 0; i < hunk.lines.length; i++) {
    if (hunk.lines[i]!.type === 'delete') deletions.push({ idx: i, line: hunk.lines[i]! });
    else if (hunk.lines[i]!.type === 'add') additions.push({ idx: i, line: hunk.lines[i]! });
  }

  // Pair adjacent deletions and additions for char-level diff
  const pairs = Math.min(deletions.length, additions.length);
  for (let p = 0; p < pairs; p++) {
    const del = deletions[p];
    const add = additions[p];
    const delContent = del!.line.content;
    const addContent = add!.line.content;

    // Find differing character ranges
    const delRanges: { start: number; end: number }[] = [];
    const addRanges: { start: number; end: number }[] = [];
    let di = 0;
    let ai = 0;

    // Find common prefix
    while (di < delContent.length && ai < addContent.length && delContent[di] === addContent[ai]) {
      di++;
      ai++;
    }
    const prefixEnd = di;

    // Find common suffix
    let dEnd = delContent.length - 1;
    let aEnd = addContent.length - 1;
    while (dEnd > di && aEnd > ai && delContent[dEnd] === addContent[aEnd]) {
      dEnd--;
      aEnd--;
    }

    if (prefixEnd <= dEnd + 1) {
      delRanges.push({ start: prefixEnd, end: dEnd + 1 });
    }
    if (prefixEnd <= aEnd + 1) {
      addRanges.push({ start: prefixEnd, end: aEnd + 1 });
    }

    if (delRanges.length > 0) results.push({ lineIndex: del!.idx, ranges: delRanges });
    if (addRanges.length > 0) results.push({ lineIndex: add!.idx, ranges: addRanges });
  }

  return results;
}
